// web/src/admin/AdminOverview.tsx (/admin/money, portal spec 22) — Money first (MRR, plans by interval, platform fees,
// sites live, model cost this month), then the master console's platform view
// (50_ADMIN_CONSOLE.md §3.1). ADMIN-ONLY: every dollar figure and every model name on this screen
// appears nowhere else in the product (02 §8).
//
// LAYOUT FOLLOWS §2's FOUR QUESTIONS, IN ORDER, because that is the order KC reads them in:
//   1. Am I making money?          → margin, MRR, AI spend, rework
//   2. Who is about to cost me?    → utilisation, complex mix, over-cap
//   3. Is the output any good?     → rework %, gate pass rate
//   4. Is anything on fire?        → needs_attention, queue depth, today
// The Stripe operator totals (D15 — MRR, commission, active customers) are kept underneath rather
// than deleted: they are a different question (Connect revenue) and they are still the only place
// commission is shown.
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { money } from '../lib/status';
import { PageHeader } from '../components/PageHeader';
import { AsyncBoundary } from '../components/AsyncBoundary';
import { Skeleton } from '../components/Skeleton';
import { KPICard } from '../components/KPICard';
import { Icon } from '../components/Icon';
import { SpendByModelChart, OutcomeChart } from './AdminCharts';
import { WindowToggle, credits, marginTone, pct, usd, useAdminWindow } from './adminShared';

export function AdminOverview() {
  const [days, setDays] = useAdminWindow();
  const m = useQuery({ queryKey: ['admin', 'metrics', days], queryFn: () => api.adminMetrics(days) });
  const c = useQuery({ queryKey: ['admin', 'charts', days], queryFn: () => api.adminCharts(days) });
  const stripe = useQuery({ queryKey: ['admin', 'overview'], queryFn: () => api.adminOverview() });

  return (
    <>
      <PageHeader
        title="Money"
        subtitle="What the plans bring in, the online service fees, and what the update runs cost. Only you see this."
      />

      {/* Portal spec 22's KPI set. Platform fees = the 25¢ online service fees (application fees), admin only. */}
      <AsyncBoundary query={stripe} skeleton={<Skeleton variant="card" count={1} />}>
        {(s) => (
          <section className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-5">
            <KPICard label="MRR (Website care)" value={money(s.mrr)} delta={`${s.activeCustomers} paying`} accent="run" />
            <KPICard label="Annual plans" value={s.annualPlans ?? '—'} delta={s.monthlyPlans !== undefined ? `${s.monthlyPlans} monthly` : undefined} accent="info" />
            <KPICard label="Platform fees this month" value={money(s.commissionMonth)} accent="run" />
            <KPICard label="Sites live" value={s.sitesLive ?? '—'} accent="info" />
            <KPICard label="Model cost this month" value={s.modelCostMonthCents !== undefined ? usd(s.modelCostMonthCents) : '—'} delta="update runs" accent="info" />
          </section>
        )}
      </AsyncBoundary>
      <h2 className="mb-3 font-head text-sm text-muted">Request engine (chalice-v2 console)</h2>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <WindowToggle days={days} onChange={setDays} />
        <Link
          to="/admin/health"
          className="inline-flex min-h-11 items-center gap-2 rounded-md border border-hair px-3 text-sm text-ink"
        >
          <Icon name="shield" size={15} />
          Health
          {!!m.data?.needsAttention && (
            <span className="rounded-pill bg-warn/15 px-2 py-0.5 text-xs text-warn">
              {m.data.needsAttention} held
            </span>
          )}
        </Link>
      </div>

      <AsyncBoundary query={m} skeleton={<Skeleton variant="card" count={3} />}>
        {(o) => (
          <>
            {/* 1 — am I making money? */}
            <section className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <KPICard
                label={`Gross margin (${o.windowDays}d)`}
                value={usd(o.grossMarginCents)}
                delta={pct(o.marginPct)}
                accent={o.grossMarginCents < 0 ? 'danger' : 'run'}
              />
              <KPICard label="MRR" value={usd(o.mrrCents)} accent="info" />
              <KPICard label={`AI spend (${o.windowDays}d)`} value={usd(o.aiSpendCents)} accent="info" />
              <KPICard
                label="Rework"
                value={pct(o.reworkPct)}
                delta={usd(o.reworkCents)}
                accent={(o.reworkPct ?? 0) > 0.2 ? 'warn' : 'info'}
              />
            </section>

            {/* 2 — who is about to cost me money? */}
            <section className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
              <KPICard
                label="Utilisation"
                value={pct(o.utilisationPct)}
                delta={`${credits(o.creditsUsed)} / ${credits(o.creditsGranted)}`}
                accent={(o.utilisationPct ?? 0) > 0.8 ? 'warn' : 'info'}
              />
              <KPICard
                label="Complex mix"
                value={pct(o.complexMixPct)}
                delta={`${o.requestsComplex} of ${o.requestsTotal}`}
                accent={(o.complexMixPct ?? 0) > 0.2 ? 'warn' : 'info'}
              />
              <KPICard
                label="Gate pass (first try)"
                value={pct(o.gatePassPct)}
                delta={`${o.gateFirstPass} of ${o.gated}`}
                accent={(o.gatePassPct ?? 1) < 0.7 ? 'warn' : 'info'}
              />
              <KPICard
                label="Over-cap requests"
                value={o.overCap}
                accent={o.overCap ? 'warn' : 'info'}
              />
            </section>

            {/* 4 — is anything on fire? */}
            <section className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
              <KPICard
                label="Needs attention"
                value={o.needsAttention}
                delta={o.needsAttention ? 'sites blocked' : undefined}
                accent={o.needsAttention ? 'danger' : 'info'}
              />
              <KPICard label="Queue — updates" value={o.queueTasks ?? '—'} accent="info" />
              <KPICard label="Queue — checks" value={o.queueGate ?? '—'} accent="info" />
              <KPICard
                label="Requests today"
                value={Object.values(o.today).reduce((s, n) => s + n, 0)}
                delta={
                  Object.entries(o.today)
                    .map(([k, n]) => `${n} ${k.replace(/_/g, ' ')}`)
                    .join(' · ') || undefined
                }
                accent="info"
              />
            </section>

            {/* 02 §2's binding ratio, checked against reality rather than assumed. A measured
                $/credit that drifts from $0.075 means either the estimates or the workload moved
                (02 §8, "cost per credit drift") and both need a response. */}
            {o.creditsUsed > 0 && (
              <p className="mt-3 text-xs text-muted">
                Measured cost per credit:{' '}
                <b className={marginTone(null)}>
                  ${(o.aiSpendCents / 100 / o.creditsUsed).toFixed(4)}
                </b>{' '}
                against the $0.0750 the model assumes (02 §2).
              </p>
            )}
          </>
        )}
      </AsyncBoundary>

      <section className="mt-6 grid gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-hair bg-surface p-4">
          <h2 className="mb-3 font-head text-sm text-muted">Daily AI spend, by model</h2>
          <AsyncBoundary query={c} skeleton={<Skeleton variant="card" count={1} />}>
            {(d) => <SpendByModelChart days={d.spend} />}
          </AsyncBoundary>
        </div>
        <div className="rounded-lg border border-hair bg-surface p-4">
          <h2 className="mb-3 font-head text-sm text-muted">Requests by outcome</h2>
          <AsyncBoundary query={c} skeleton={<Skeleton variant="card" count={1} />}>
            {(d) => <OutcomeChart days={d.outcomes} />}
          </AsyncBoundary>
        </div>
      </section>

    </>
  );
}
